import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import { splitByActive } from "../lib/activeList.js";
import { Sheet } from "../components/Sheet.js";
import type {
  ManagedEmployee,
  Role,
  Employee,
  ActivityVisibility,
} from "../types.js";

/**
 * The staff, and their PINs and roles.
 *
 * Lives on the dashboard rather than in Manage, decided 2026-09-07, and
 * therefore behind ADMIN — the server enforces the same on the three
 * `/api/manage/employees` routes, so this is not a hidden button
 * pretending to be a restriction.
 *
 * What that costs and why it is written down: a lead can no longer reset
 * the PIN of somebody locked out mid-shift, nor fix a name collision
 * that is booking one person's work to another. Both are now an admin's
 * job. The shop chose that knowingly.
 *
 * Nothing here deletes. A leaver is deactivated: they come off the
 * sign-in grid and everything they recorded stays.
 */
export function PeoplePanel({ me }: { me: Employee }) {
  const [employees, setEmployees] = useState<ManagedEmployee[]>([]);
  // `forId`: the answer belongs to one person's row and is said there.
  // At the top of a thirty-row list it was off-screen on the tablet, and
  // an admin who saw nothing tapped New PIN again — replacing the PIN
  // they had not yet read.
  const [error, setError] = useState<{ message: string; forId?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // A generated PIN is shown once and cannot be read back, so it stays
  // on screen until it is dismissed rather than fading away.
  const [issued, setIssued] = useState<{ name: string; pin: string; forId?: string } | null>(
    null,
  );

  const [newPerson, setNewPerson] = useState({
    displayName: "",
    role: "EMPLOYEE" as Role,
  });
  // Deactivated people are hidden until asked for, the same as retired
  // tasks and revoked devices. This is the list that grows on its own:
  // every leaver stays for good, because deactivating is what keeps
  // their measurements, so after a year the roster is mostly people who
  // do not work here.
  const [showRetired, setShowRetired] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [roleDraft, setRoleDraft] = useState<Role>("EMPLOYEE");
  // Which person's activity tiles are being edited, and their list.
  // Loaded on demand rather than with the roster: thirty people times
  // nineteen tasks is a lot to fetch for a panel that is mostly used to
  // reset a PIN.
  const [tilesFor, setTilesFor] = useState<ManagedEmployee | null>(null);
  const [tiles, setTiles] = useState<ActivityVisibility[] | null>(null);

  const { listed, hiddenCount } = splitByActive(employees, showRetired);

  const load = useCallback(async () => {
    try {
      const { employees: list } = await api.manageEmployees();
      setEmployees(list);
    } catch {
      setError({ message: "Could not load the people." });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (work: () => Promise<void>, whenFailed: string, forId?: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      // The server's error codes are the keys, so a new failure mode
      // gets a code and an entry here rather than a sentence in a route.
      const known: Record<string, string> = {
        name_taken: "That name is already taken.",
        name_required: "A name cannot be blank.",
        admin_required: "Only an admin can do that.",
        cannot_deactivate_self: "You cannot deactivate yourself.",
        cannot_change_own_role: "You cannot change your own role.",
        // Reachable only by two admins removing each other at once. An
        // admin is the only role that can appoint another, so leaving
        // none has no fix inside the app.
        last_admin: "That is the last admin. Appoint another one first.",
      };
      setError({ message: known[code] ?? whenFailed, ...(forId ? { forId } : {}) });
    } finally {
      setBusy(false);
    }
  };

  const addPerson = (event: React.FormEvent) => {
    event.preventDefault();
    if (!newPerson.displayName.trim()) return;
    void run(async () => {
      const { employee, pin } = await api.addEmployee(
        newPerson.displayName.trim(),
        newPerson.role,
      );
      setIssued({ name: employee.displayName, pin });
      setNewPerson({ displayName: "", role: "EMPLOYEE" });
    }, "Could not add the person.");
  };

  const roleOptions = (
    <>
      <option value="EMPLOYEE">Employee</option>
      <option value="LEAD">Lead — starts tasks and prints tags</option>
      {me.role === "ADMIN" ? (
        <option value="ADMIN">Admin — also registers devices</option>
      ) : null}
    </>
  );

  // One card, said wherever the PIN was asked for: under Add someone
  // for a new person, in the row for a reset.
  const issuedCard = issued ? (
    <section className="card issued">
      <p>PIN for {issued.name} — shown once</p>
      <div className="row">
        <code className="huge">{issued.pin}</code>
        <button type="button" className="primary" onClick={() => setIssued(null)}>
          Done
        </button>
      </div>
    </section>
  ) : null;

  const closeTiles = () => {
    setTilesFor(null);
    setTiles(null);
  };

  return (
    <>
      {/* Inside the sheet instead while one is open — behind the overlay
          an error is a failure nobody sees. */}
      {error && !error.forId && !tilesFor ? (
        <p className="error" role="alert">{error.message}</p>
      ) : null}

      {issued && !issued.forId ? issuedCard : null}

      <section className="card">
        <h2>Add someone</h2>
        <form onSubmit={addPerson} className="field-action">
          <div className="pair">
            <label>
              Name
              <input
                type="text"
                value={newPerson.displayName}
                onChange={(e) =>
                  setNewPerson({ ...newPerson, displayName: e.target.value })
                }
                placeholder="Name on the sign-in grid"
                aria-label="Display name"
              />
            </label>
            <label>
              Role
              <select
                value={newPerson.role}
                onChange={(e) =>
                  setNewPerson({ ...newPerson, role: e.target.value as Role })
                }
              >
                {roleOptions}
              </select>
            </label>
          </div>
          <button
            type="submit"
            className="primary"
            disabled={busy || !newPerson.displayName.trim()}
          >
            Add
          </button>
        </form>
      </section>

      <section className="card">
        <h2>People</h2>
        <ul className="joblist">
          {listed.map((person) => (
            <li key={person.id} className={person.active ? "" : "retired"}>
              {renaming === person.id ? (
                <form
                  className="stack"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void run(async () => {
                      await api.updateEmployee(person.id, {
                        displayName: renameDraft.trim(),
                        ...(roleDraft !== person.role ? { role: roleDraft } : {}),
                      });
                      setRenaming(null);
                    }, "Could not save the changes.", person.id);
                  }}
                >
                  <input
                    type="text"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    aria-label="Display name"
                    autoFocus
                  />
                  {/* People are promoted. Editing the role here rather
                      than only at creation saves deactivating somebody
                      and adding them again, which would split one
                      person's measurements across two rows.

                      Hidden for your own row: the server refuses it,
                      because an admin who demotes themselves cannot
                      appoint another one and has no way back. */}
                  {person.id === me.id ? null : (
                    <label>
                      Role
                      <select
                        value={roleDraft}
                        onChange={(e) => setRoleDraft(e.target.value as Role)}
                        disabled={person.role === "ADMIN" && me.role !== "ADMIN"}
                      >
                        {roleOptions}
                      </select>
                    </label>
                  )}
                  <span className="row-actions">
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => setRenaming(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="primary"
                      disabled={busy || renameDraft.trim() === ""}
                    >
                      Save
                    </button>
                  </span>
                </form>
              ) : (
                <>
                  <div className="item-text">
                    <span className="item-title">
                      {person.displayName}
                      {person.id === me.id ? <span className="muted"> · you</span> : null}
                      {person.role !== "EMPLOYEE" ? (
                        <span className="badge accent">{person.role.toLowerCase()}</span>
                      ) : null}
                      {!person.active ? <span className="badge">inactive</span> : null}
                    </span>
                  </div>
                  <span className="row-actions">
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => {
                        setRenaming(person.id);
                        setRenameDraft(person.displayName);
                        setRoleDraft(person.role);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          const { pin } = await api.updateEmployee(person.id, {
                            resetPin: true,
                          });
                          if (pin) setIssued({ name: person.displayName, pin, forId: person.id });
                        }, "Could not reset the PIN.", person.id)
                      }
                    >
                      New PIN
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          setTilesFor(person);
                          setTiles(null);
                          const { activities } = await api.activitiesFor(person.id);
                          setTiles(activities);
                        }, "Could not load the activities.")
                      }
                    >
                      Activities
                    </button>
                    <button
                      type="button"
                      className={person.active ? "caution" : "ghost"}
                      disabled={busy || person.id === me.id}
                      onClick={() =>
                        void run(
                          () =>
                            api
                              .updateEmployee(person.id, { active: !person.active })
                              .then(() => undefined),
                          "Could not change the person.",
                          person.id,
                        )
                      }
                    >
                      {person.active ? "Deactivate" : "Restore"}
                    </button>
                  </span>
                </>
              )}
              {error?.forId === person.id && !tilesFor ? (
                <p className="error row-answer" role="alert">{error.message}</p>
              ) : null}
              {issued?.forId === person.id ? issuedCard : null}
            </li>
          ))}
        </ul>

        {/* The same switch as the task and device lists, and the same
            reason for the count: deactivating a row while they are
            hidden makes it vanish, which is exactly what a delete looks
            like on a screen whose whole point is that nothing is
            deleted. No empty-list case is needed — you cannot
            deactivate yourself, so at least your own row is always
            here. */}
        {hiddenCount > 0 ? (
          <label className="list-toggle">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show deactivated people ({hiddenCount})
          </label>
        ) : null}

        <p className="hint">
          Deactivating takes someone off the sign-in grid. Their recorded
          work stays. Renaming changes the tile only — every session
          already recorded stays attached to the same person, so use it
          to tell two similar names apart rather than adding a second
          account.
        </p>
      </section>

      {/* The activity editor. A ticked box means the person sees that
          tile, which is the way round somebody reads a list of
          checkboxes — the table stores the opposite, the exceptions, and
          the service does that translation. */}
      {tilesFor ? (
        <Sheet
          title={`What ${tilesFor.displayName} sees on the grid`}
          // The mirror of the count on the task sheet. Two numbers that
          // have to agree are a cheap way to notice when a mental model
          // has drifted from the data.
          subtitle={
            tiles ? `${tiles.filter((t) => !t.hidden).length} of ${tiles.length} tiles shown` : undefined
          }
          onClose={closeTiles}
        >
          {error ? <p className="error" role="alert">{error.message}</p> : null}
          {tiles === null ? (
            <p className="muted">Loading…</p>
          ) : tiles.length === 0 ? (
            <p className="muted">No generic tasks set up.</p>
          ) : (
            <>
              <ul className="checklist">
                {tiles.map((t) => (
                  <li key={t.taskTypeId}>
                    <label className={t.hidden ? "off" : undefined}>
                      <input
                        type="checkbox"
                        checked={!t.hidden}
                        disabled={busy}
                        onChange={() =>
                          setTiles((current) =>
                            (current ?? []).map((row) =>
                              row.taskTypeId === t.taskTypeId
                                ? { ...row, hidden: !row.hidden }
                                : row,
                            ),
                          )
                        }
                      />
                      <span className="tile-text">
                        <span>{t.name}</span>
                        {t.description ? (
                          <span className="tile-note">{t.description}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              <p className="hint">
                Unticking hides the tile from this person only. It
                changes nothing already recorded, and does not stop a
                task they are running right now.{" "}
                <strong>
                  Leave a tile ticked if there is any chance they do
                  that work
                </strong>{" "}
                — somebody with nowhere to record what they did taps
                the nearest tile instead, and nothing on any screen
                would show that. The same setting per task, across
                everybody at once, is <strong>Who sees this</strong>{" "}
                on the Tasks screen.
              </p>
            </>
          )}
          <div className="sheet-actions">
            <button
              type="button"
              className="ghost"
              disabled={busy || tiles === null}
              onClick={() =>
                setTiles((current) =>
                  (current ?? []).map((row) => ({ ...row, hidden: false })),
                )
              }
            >
              Show all
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="primary"
              disabled={busy || tiles === null}
              onClick={() =>
                void run(async () => {
                  const hidden = (tiles ?? [])
                    .filter((t) => t.hidden)
                    .map((t) => t.taskTypeId);
                  const { activities } = await api.setActivities(tilesFor.id, hidden);
                  setTiles(activities);
                }, "Could not save the activities.")
              }
            >
              Save
            </button>
          </div>
        </Sheet>
      ) : null}
    </>
  );
}
